import React, { useCallback, useEffect, useRef, useState } from "react";
import AppLayout from "../layouts/AppLayout";
import Product from "../components/Product";
import Pagination from "../components/Pagination";
import Filters from "../components/Filters";
import Frontend from "../../helpers/Frontend";
import { Head } from "@inertiajs/react";
import MobileFilters from "../components/MobileFilters";
import Card from "../components/Card";
import Faq from "../components/Faq";
import SimilarCollection from "../components/SimilarCollection";
import Advantages from "../components/Advantages";

export default function Collection({ products: initialProducts, similarCollections, collection, faq, cards, filters: initialFilters, filterValues: initialFilterValues, category }) {
    const title = collection.name;

    const [products, setProducts] = useState(initialProducts)
    const [filters, setFilters] = useState(initialFilters)
    const [filterValues, setFilterValues] = useState(initialFilterValues)
    const initialLoad = useRef(true);

    const [loading, setLoading] = useState(false);
    const inStock = useRef<boolean>(true);

    const [page, setPage] = useState(initialProducts.current_page)
    const [total, setTotal] = useState(initialProducts.total)

    const abortController = useRef<AbortController>();

    const getProducts = useCallback((page, filters) => {
        setLoading(true);

        filters['in-stock'] = inStock.current

        if (abortController.current) {
            abortController.current.abort()
        }
        abortController.current = new AbortController();

        Frontend.getCollection(collection.alias, 10, page, '', filters, { signal: abortController.current?.signal }).then((response) => {
            setProducts(response)
            setPage(response.current_page)
            setTotal(response.total)
            setFilters(response.filters)

            setLoading(false)
        }).catch((error) => {
            if (error.name === 'AbortError') {
                return;
            }

            setLoading(false);
        });
    }, [])

    const filterCallback = useCallback((values) => {
        // let finalValues: Array<any> = [];

        // Object.keys(values).forEach((key) => {
        //     finalValues[key] = [];

        //     Object.keys(values[key]).forEach((id) => {
        //         if (values[key][id] === true) {
        //             finalValues[key].push(Number(id))
        //         }
        //     })
        // })

        setPage(1)
        setFilterValues(values)
    }, [])

    const toggleInStock = () => {
        inStock.current = !inStock.current

        if (page > 1) {
            setPage(1)
        } else {
            getProducts(page, filterValues)
        }
    }

    useEffect(() => {
        if (initialLoad.current === false) {
            getProducts(page, filterValues)
        }

        initialLoad.current = false;
    }, [page, filterValues, getProducts])

    return (
        <AppLayout>
            <Head>
                <title>{`${title} - Compare Prices in Canada | PriceHunt.ca`}</title>
                <meta head-key="description" name="description" content={`Looking for the best ${title} deals in Canada? Updated daily. Find the best deal on PriceHunt.ca.`} />
            </Head>
            <div className="w-full">
                <div className="container max-w-dasha mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-0 flex flex-col space-y-7">
                    <div className="flex flex-col gap-4 md:gap-0 md:flex-row justify-between md:items-center">
                        <div>
                            <h2 className="text-3xl font-medium">{title}</h2>
                            <p className="text-gray-600 mt-1 max-w-2xl">{collection.short_description}</p>
                        </div>
                        <div className="flex justify-between md:justify-end items-center gap-4">
                            <MobileFilters
                                filters={filters}
                                values={filterValues}
                                onApply={(next) => { setPage(1); setFilterValues(next); }}
                                onClearAll={() => { setPage(1); setFilterValues({}); }}
                            />
                            <div className="flex gap-4 items-center">
                                <div>
                                    {loading
                                        ? null : <>
                                            <span>{total} results</span>
                                        </>
                                    }

                                </div>
                                <label className="inline-flex items-center cursor-pointer">
                                    <input type="checkbox" value="" onChange={toggleInStock} defaultChecked={inStock.current} className="sr-only peer" />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                                    <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">In stock</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-5">
                        {cards ? cards.map((card) => {
                            return <Card name={card.name} value={card.value} />
                        }) : null}
                    </div>
                    <div className="flex gap-7">
                        <div className="w-[200px] hidden md:block">
                            {/* <Filters filters={filters} values={filterValues} onCallback={filterCallback} /> */}
                            <Filters
                                filters={filters}
                                values={filterValues}
                                onCallback={filterCallback}
                            />
                        </div>
                        <div className="flex-col flex-1 space-y-7">
                            {loading ? (
                                <div className="grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg p-6 lg:overflow-visible">
                                    <svg className="text-blue-300 w-12 h-12 animate-spin" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg"
                                        width="24" height="24">
                                        <path
                                            d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
                                            stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"></path>
                                        <path
                                            d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
                                            stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" className="text-blue-500">
                                        </path>
                                    </svg>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                                        {products ? products.data.map((product) => {
                                            return <Product product={product} />
                                        }) : null}
                                    </div>

                                    <div className="flex justify-center">
                                        <Pagination page={page} total={products.last_page} callback={setPage} route={'/'} />
                                    </div>
                                </>
                            )}

                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2">
                        {faq ? faq.map((faqItem) => {
                            return <Faq question={faqItem.question} answer={faqItem.answer} />
                        }) : null}
                    </div>
                    {similarCollections && similarCollections.length > 0 ? (<div>
                        <h2 className="text-3xl font-medium mb-7">Similar collections</h2>
                        <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-5">
                            {similarCollections ? similarCollections.map((similarCollection) => {
                                return <SimilarCollection collection={similarCollection} />
                            }) : null}
                        </div>
                    </div>) : null}

                </div>
            </div>
            <hr />
            <Advantages />
            <hr />
        </AppLayout>
    )
}